import omit from 'lodash/omit'
import uniq from 'lodash/uniq'

import { getPipetteSpecsV2 } from '@opentrons/shared-data'

import {
  DEFAULT_MM_OFFSET_FROM_BOTTOM,
  INITIAL_DECK_SETUP_STEP_ID,
} from '../constants'
import {
  createFile,
  getFileMetadata,
  getRobotStateTimeline,
} from '../file-data/selectors'
import {
  getArgsAndErrorsByStepId,
  getPipetteEntities,
  getSavedStepForms,
} from '../step-forms/selectors'
import { trackEvent } from './mixpanel'
import { getHasOptedIn } from './selectors'
import { flattenNestedProperties } from './utils/flattenNestedProperties'

import type { Middleware } from 'redux'
import type {
  ConsolidateArgs,
  DistributeArgs,
  FlexStackerFillItemsArgs,
  MixArgs,
  NormalizedPipetteById,
  TransferArgs,
} from '@opentrons/step-generation'
import type { FormData, StepIdType, StepType } from '../form-types'
import type { RenameStepAction } from '../labware-ingred/actions'
import type { LocationUpdate } from '../load-file/migration/utils/getAdditionalEquipmentLocationUpdate'
import type { CreatePipettesAction } from '../step-forms/actions'
import type { StepArgsAndErrors } from '../steplist'
import type { BaseState } from '../types'
import type { DuplicateSelectedStepsAction } from '../ui/steps'
import type { SaveStepFormAction } from '../ui/steps/actions/thunks'
import type { AnalyticsEventAction } from './actions'
import type { AnalyticsEvent } from './mixpanel'

const DEFAULT_VALUE = 'default'
const PIPETTING_ARGS_FILTER_LIST = [
  'touchTipAfterAspirateOffsetMmFromBottom',
  'touchTipAfterDispenseOffsetMmFromBottom',
  'commandCreatorFnName',
  'blowoutFlowRateUlSec',
  'blowoutOffsetFromTopMm',
  'touchTipMmFromBottom',
  'aspirateAirGapVolume',
  'aspirateFlowRateUlSec',
  'dispenseFlowRateUlSec',
]

interface TransformedPipetteInfo {
  [pipetteId: string]: {
    name: string
    numberOfTipracks: number
  }
}

// Converts Redux actions to analytics events (read: Mixpanel events).
// Returns null if there is no analytics event associated with the action,
// which happens for most actions.
export const reduxActionToAnalyticsEvent = (
  state: BaseState,
  action: any
): AnalyticsEvent | null => {
  if (action.type === 'SAVE_STEP_FORM') {
    // create the "saveStep" action, taking advantage of the formToArgs machinery
    // to get nice cleaned-up data instead of the raw form data.
    const a: SaveStepFormAction = action

    const argsAndErrors: StepArgsAndErrors =
      getArgsAndErrorsByStepId(state)[a.payload.form.id]

    const { stepArgs } = argsAndErrors

    if (stepArgs !== null) {
      const pipetteEntities = getPipetteEntities(state)
      const fileMetadata = getFileMetadata(state)
      const dateCreatedTimestamp = fileMetadata.created

      // additional fields for analytics, eg descriptive name for pipettes
      // (these fields are prefixed with double underscore only to make sure they
      // never accidentally overlap with actual fields)
      const additionalProperties = flattenNestedProperties(
        stepArgs as unknown as Record<string, unknown>
      )

      // Mixpanel wants YYYY-MM-DDTHH:MM:SS for Date type
      additionalProperties.__dateCreated =
        dateCreatedTimestamp != null && Number.isFinite(dateCreatedTimestamp)
          ? new Date(dateCreatedTimestamp).toISOString()
          : null

      additionalProperties.__protocolName = fileMetadata.protocolName
      if ('pipette' in stepArgs && stepArgs.pipette != null) {
        additionalProperties.__pipetteName =
          pipetteEntities[stepArgs?.pipette].name
      }

      const stepName = stepArgs.commandCreatorFnName
      const modifiedStepName =
        stepName === 'delay' || stepName === 'waitForTemperature'
          ? 'pause'
          : stepName

      switch (modifiedStepName) {
        case 'engageMagnet':
        case 'disengageMagnet': {
          return {
            name: `magnetStep`,
            properties: { type: modifiedStepName },
          }
        }
        case 'setTemperature':
        case 'deactivateTemperature': {
          return {
            name: `temperatureStep`,
            properties: { type: modifiedStepName },
          }
        }
        case 'thermocyclerProfile':
        case 'thermocyclerState': {
          return {
            name: 'thermocyclerStep',
            properties: { type: modifiedStepName },
          }
        }
        case 'heaterShaker': {
          return {
            name: 'heaterShakerStep',
            properties: {},
          }
        }
        case 'transfer':
        case 'consolidate':
        case 'distribute': {
          const stepArgModified = omit(
            stepArgs as TransferArgs | ConsolidateArgs | DistributeArgs,
            PIPETTING_ARGS_FILTER_LIST
          )
          return {
            name: `${modifiedStepName}Step`,
            properties: {
              ...stepArgModified,
              aspirateAirGap:
                stepArgModified.aspirateAirGapVolume ?? DEFAULT_VALUE,
              aspirateFlowRate:
                stepArgModified.aspirateFlowRateUlSec ?? DEFAULT_VALUE,
              dispenseFlowRate:
                stepArgModified.dispenseFlowRateUlSec ?? DEFAULT_VALUE,
              dispenseAirGap:
                stepArgModified.dispenseAirGapVolume ?? DEFAULT_VALUE,
              blowoutFlowRate: stepArgModified.blowoutFlowRateUlSec,
              aspirateOffsetFromBottomMm:
                stepArgModified.aspirateOffsetFromBottomMm ===
                DEFAULT_MM_OFFSET_FROM_BOTTOM
                  ? DEFAULT_VALUE
                  : stepArgModified.aspirateOffsetFromBottomMm,
              dispenseOffsetFromBottomMm:
                stepArgModified.dispenseOffsetFromBottomMm ===
                DEFAULT_MM_OFFSET_FROM_BOTTOM
                  ? DEFAULT_VALUE
                  : stepArgModified.dispenseOffsetFromBottomMm,
              aspirateXOffset:
                stepArgModified.aspirateXOffset === 0
                  ? DEFAULT_VALUE
                  : stepArgModified.aspirateXOffset,
              aspirateYOffset:
                stepArgModified.aspirateYOffset === 0
                  ? DEFAULT_VALUE
                  : stepArgModified.aspirateYOffset,
              dispenseXOffset:
                stepArgModified.dispenseXOffset === 0
                  ? DEFAULT_VALUE
                  : stepArgModified.dispenseXOffset,
              dispenseYOffset:
                stepArgModified.dispenseYOffset === 0
                  ? DEFAULT_VALUE
                  : stepArgModified.dispenseYOffset,

              ...additionalProperties,
            },
          }
        }
        case 'mix': {
          const stepArgModified = omit(
            stepArgs as MixArgs,
            PIPETTING_ARGS_FILTER_LIST
          )
          return {
            name: `mixStep`,
            properties: {
              ...stepArgModified,
              aspirateFlowRate:
                stepArgModified.aspirateFlowRateUlSec ?? DEFAULT_VALUE,
              dispenseFlowRate:
                stepArgModified.dispenseFlowRateUlSec ?? DEFAULT_VALUE,
              blowoutFlowRate: stepArgModified.blowoutFlowRateUlSec,
              // TODO: Include positionReference in Mixpanel as well.
              // TODO: Rename offsetFromBottomMm -> zOffset in Mixpanel to be consistent with our internal variable names.
              offsetFromBottomMm:
                stepArgModified.zOffset === DEFAULT_MM_OFFSET_FROM_BOTTOM
                  ? DEFAULT_VALUE
                  : stepArgModified.zOffset,
              xOffset:
                stepArgModified.xOffset === 0
                  ? DEFAULT_VALUE
                  : stepArgModified.xOffset,
              yOffset:
                stepArgModified.yOffset === 0
                  ? DEFAULT_VALUE
                  : stepArgModified.yOffset,
              ...additionalProperties,
            },
          }
        }
        case 'flexStackerEmpty':
          return {
            name: 'flexStackerEmptyStep',
            properties: {},
          }
        case 'flexStackerRetrieve':
          return {
            name: 'flexStackerRetrieveStep',
            properties: {},
          }
        case 'flexStackerFillItems':
          const args = stepArgs as FlexStackerFillItemsArgs
          return {
            name: 'flexStackerFillItemsStep',
            properties: {
              labwareUri: args.fillLabwareUri,
            },
          }
        case 'flexStackerStore':
          return {
            name: 'flexStackerStoreStep',
            properties: {},
          }
        default:
          return {
            name: `${modifiedStepName}Step`,
            properties: { ...stepArgs, ...additionalProperties },
          }
      }
    }
  }

  if (action.type === 'COMPUTE_ROBOT_STATE_TIMELINE_SUCCESS') {
    const robotTimeline = getRobotStateTimeline(state)
    const { errors, timeline } = robotTimeline
    const commandAndRobotState = timeline.filter(t => t.warnings != null)
    const warnings =
      commandAndRobotState.length > 0
        ? commandAndRobotState.flatMap(state => state.warnings ?? [])
        : []
    if (errors != null) {
      const errorTypes = errors.map(error => error.type)
      return {
        name: 'timelineErrors',
        properties: { errorTypes },
      }
    }
    if (warnings.length > 0) {
      return {
        name: 'timelineWarnings',
        properties: { warnings },
      }
    }
  }
  if (action.type === 'SAVE_STEP_FORMS_MULTI') {
    const fileMetadata = getFileMetadata(state)
    const dateCreatedTimestamp = fileMetadata.created

    const { editedFields, stepIds } = action.payload
    const additionalProperties = flattenNestedProperties(
      editedFields as Record<string, unknown>
    )
    const savedStepForms = getSavedStepForms(state)
    const batchEditedStepForms: FormData[] = stepIds.map(
      (id: StepIdType) => savedStepForms[id]
    )
    let stepType = null
    const uniqueStepTypes: StepType[] = uniq(
      batchEditedStepForms.map(form => form.stepType)
    )
    if (uniqueStepTypes.length === 1) {
      stepType = uniqueStepTypes[0]
    } else {
      console.warn(
        `Something went wrong, expected one step type in the batch edit form, but got ${String(
          uniqueStepTypes
        )} `
      )
    }

    additionalProperties.stepType = stepType

    // (these fields are prefixed with double underscore only to make sure they
    // never accidentally overlap with actual fields)
    // Mixpanel wants YYYY-MM-DDTHH:MM:SS for Date type
    additionalProperties.__dateCreated =
      dateCreatedTimestamp != null && Number.isFinite(dateCreatedTimestamp)
        ? new Date(dateCreatedTimestamp).toISOString()
        : null

    additionalProperties.__protocolName = fileMetadata.protocolName

    return {
      name: 'saveStepsMulti',
      properties: { ...editedFields, ...additionalProperties },
    }
  }
  if (action.type === 'DELETE_MULTIPLE_STEPS') {
    return {
      name: 'deleteMultipleSteps',
      properties: {},
    }
  }
  if (action.type === 'DUPLICATE_SELECTED_STEPS') {
    const a: DuplicateSelectedStepsAction = action
    if (a.payload.steps.length > 1) {
      return {
        name: 'duplicateMultipleSteps',
        properties: {},
      }
    }
  }
  if (action.type === 'LOAD_FILE') {
    return {
      name: 'loadFile',
      properties: {},
    }
  }
  if (action.type === 'GENERATE_NEW_PROTOCOL') {
    return {
      name: 'createNewProtocol',
      properties: {},
    }
  }
  if (action.type === 'CHANGE_STEP_DETAILS') {
    const a: RenameStepAction = action
    if ('stepDetails' in a.payload.update) {
      return {
        name: 'editStepMetadata',
        properties: {
          stepDetails: a.payload.update.stepDetails,
          stepName: a.payload.update.stepName,
        },
      }
    }
  }
  if (action.type === 'SAVE_PROTOCOL_FILE') {
    const file = createFile(state)
    const { metadata, robot, designerApplication } = file.designerApplication
    const { ingredients, savedStepForms, modules, pipettes, labware } =
      designerApplication.data

    const robotType = { robotType: robot.model }
    const pipetteDisplayNames = Object.values(pipettes).map(
      pipette => getPipetteSpecsV2(pipette.pipetteName)?.displayName
    )
    const moduleModels = Object.values(modules).map(module => module.model)
    const initialStep: Record<string, LocationUpdate> =
      savedStepForms[INITIAL_DECK_SETUP_STEP_ID]

    const {
      trashBinLocationUpdate,
      wasteChuteLocationUpdate,
      gripperLocationUpdate,
      stagingAreaLocationUpdate,
      labwareLocationUpdate,
    } = initialStep

    const hasTrashBin = Object.keys(trashBinLocationUpdate).length > 0
    const hasWasteChute = Object.keys(wasteChuteLocationUpdate).length > 0
    const hasGripper = Object.keys(gripperLocationUpdate).length > 0
    const numberOfSteps = {
      numberOfSteps: Object.keys(savedStepForms).length - 1,
    }
    const stagingAreaSlots = Object.values(stagingAreaLocationUpdate).map(
      location => location
    )

    const labwareInfo = Object.entries(labwareLocationUpdate).reduce(
      (acc: Record<string, { location: string }>, [id, location]) => {
        const displayName = labware[id].displayName
        acc[displayName] = { location }
        return acc
      },
      {}
    )

    const liquidClasses = {
      liquidClasses: Object.values(ingredients).map(ingredient =>
        'liquidClass' in ingredient && ingredient.liquidClass != null
          ? ingredient.liquidClass
          : 'none'
      ),
    }

    const fixtureInfo = {
      trashBin: hasTrashBin,
      wasteChute: hasWasteChute,
      stagingAreaSlots: stagingAreaSlots,
      gripper: hasGripper,
    }

    const loadCommandInfo = {
      modules: moduleModels,
      pipettes: pipetteDisplayNames,
    }

    return {
      name: 'saveProtocol',
      properties: {
        ...metadata,
        ...loadCommandInfo,
        ...robotType,
        ...liquidClasses,
        ...numberOfSteps,
        ...fixtureInfo,
        ...labwareInfo,
      },
    }
  }

  if (action.type === 'CREATE_PIPETTES') {
    const a: CreatePipettesAction = action

    const getTransformPipetteInfo = (
      payload: NormalizedPipetteById
    ): TransformedPipetteInfo => {
      return Object.entries(payload).reduce(
        (acc: TransformedPipetteInfo, [pipetteId, { name, tiprackDefURI }]) => {
          acc[pipetteId] = {
            name,
            numberOfTipracks: tiprackDefURI.length,
          }
          return acc
        },
        {}
      )
    }
    const properties = getTransformPipetteInfo(a.payload)
    return {
      name: 'numberOfTipracksPerPipette',
      properties,
    }
  }
  if (action.type === 'ANALYTICS_EVENT') {
    const a: AnalyticsEventAction = action
    return a.payload
  }
  return null
}

export const trackEventMiddleware: Middleware<BaseState, any> =
  ({ getState, dispatch }) =>
  next =>
  action => {
    const result = next(action)

    // NOTE: this is the Redux state AFTER the action has been fully dispatched
    const state = getState()

    const optedIn = getHasOptedIn(state as BaseState)?.hasOptedIn ?? false
    const event = reduxActionToAnalyticsEvent(state as BaseState, action)

    if (event != null) {
      // actually report to analytics (trackEvent is responsible for using optedIn)
      trackEvent(event, optedIn)
    }
    return result
  }
